import { arrayOfAll } from 'neuro-utils';
import * as React from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { useAppSelector as useSelector } from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';
import { fetchWithOptions } from 'Utility/fetch';
import flattenMessages from 'Utility/flattenMessages';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import enMessages from '../../../packages/neuro-localizations/l18n/en.json';
import fiMessages from '../../../packages/neuro-localizations/l18n/fi.json';
import { allDiagnoses } from '../../routes/Diagnosis/utils/definitions';

const locales = arrayOfAll.options<Locale>()(['fi', 'en']);
export const isLocale = (locale: unknown): locale is Locale => (locales as unknown[]).includes(locale);

export type TMessages = { [key in Locale]: { [key in LocalizationKey]: string } };
const _messages = {
  fi: flattenMessages(fiMessages),
  en: flattenMessages(enMessages),
} as TMessages;

export const localeDefault = 'fi';

export const LocalizerCtx = React.createContext<{
  messages: TMessages;
  setLocale?: (locale: Locale) => void;
  availableLocales: Array<Locale>;
  locale?: Locale;
}>({
  messages: _messages,
  availableLocales: locales,
});

export const FormatMessageContext = React.createContext<{
  formatMessage: (
    id: string,
    /** Whether to remove the localization path, leaving only the part of the id */
    removeLocalePath?: boolean,
    /** Whether to skip checking if locale id exists, and always warn about missing ids */
    warnAboutMissingLocale?: boolean,
  ) => string;
}>({
  formatMessage: () => '',
});

const UseWithFormatMessageContext = ({ children }: { children: React.JSX.Element }) => {
  const intl = useIntl();
  const formatMessage = (id: string, removeLocalePath = false, warnAboutMissingLocale = false): string => {
    // If id is for some reason undefined, or missing in some way, it might cause an error and crash the UI when trying to localize it
    if (!id) return id;

    if (warnAboutMissingLocale) return intl.formatMessage({ id });

    const localeKeys = Object.keys(intl.messages);
    if (!localeKeys.includes(id)) {
      // If localized messages do not have the given id, then return the id
      if (removeLocalePath) {
        // If removeLocalePath is true, then remove everything separated by . except the last id
        const path = id.split('.');
        return path.length > 0 ? (path[path.length - 1] ?? id) : id;
      } else return id;
    }

    return intl.formatMessage({ id });
  };
  return <FormatMessageContext value={{ formatMessage }}>{children}</FormatMessageContext>;
};

/**
 * Component in charge of maintaining the state of diagnosis localization strings in the UI.
 * Any localization missing from hard-coded values (diagnosis.opts.{code}) is fetched from
 * ICD-10 service. To override fetching, i.e. prevent it from happening, define a hard-coded
 * value in the corresponding package (neuro-localizations/l18n/{fi or en}.json).
 */
const Localizer = (props: { children: React.JSX.Element }): React.JSX.Element => {
  const [messages, setMessages] = React.useState<TMessages>(_messages);
  const [locale, setLocale] = React.useState<Locale>(browserLanguage);

  const uiLanguage = useSelector((state: IState) => state.settings.userSettings.uiLanguage);

  React.useEffect(() => {
    if (uiLanguage && locale !== uiLanguage) setLocale(uiLanguage);
  }, [uiLanguage]);

  /* On first mount find any missing diagnosis (dx) localizations, query them
  from ICD-10 service and then set the received results in state. */
  React.useEffect(() => {
    const missingDxLocalizations: TDiagnosis[] = [];
    allDiagnoses.forEach(dx => {
      if (!messages[locale][`diagnosis.opts.${dx}`]) missingDxLocalizations.push(dx);
    });
    // none missing -> do nothing; return
    if (missingDxLocalizations.length < 1) return;
    // otherwise query each missing dx code from ICD-10 service
    const queryArray: Promise<ICD10Item[]>[] = [];
    missingDxLocalizations.forEach(dx => {
      const queryString = dx.replace('#', '');
      queryArray.push(
        fetchWithOptions(
          `/api/icd-code-search/v1/search?search=${queryString}&locale=${locale}`,
          { neurojwt: parseJWTFromCookie() },
          { method: 'GET' },
        ).then(res => {
          if (res.status === 200) return res.json();
          else return [] as ICD10Item[];
        }),
      );
    });
    // once all are fetched, set the received results in state
    Promise.all(queryArray)
      .then(responses => {
        const newMessages = { ...messages };
        responses.forEach((dxQueryResult, i) => {
          const dxCode = missingDxLocalizations[i];
          const fetchedDescription = dxQueryResult.find(r => r.icd10code === dxCode)?.icd10description;
          newMessages[locale][`diagnosis.opts.${dxCode}`] = fetchedDescription
            ? `${dxCode} ${fetchedDescription}`
            : (dxCode ?? ''); // fallback to the ICD-10 code only
        });
        setMessages(newMessages);
      })
      .catch(() => {
        return;
      });
  }, [locale]);

  const setNewLocale = (newLocale: Locale) => {
    if (newLocale !== locale) {
      setLocale(newLocale);
    }
  };

  return (
    <LocalizerCtx.Provider value={{ messages, availableLocales: locales, setLocale: setNewLocale, locale }}>
      <LocalizerCtx.Consumer>
        {(value): React.JSX.Element => (
          <IntlProvider
            locale={locale}
            messages={value.messages[locale]}
            onError={error => makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'localize'))}
          >
            <UseWithFormatMessageContext>{props.children}</UseWithFormatMessageContext>
          </IntlProvider>
        )}
      </LocalizerCtx.Consumer>
    </LocalizerCtx.Provider>
  );
};

export default Localizer;
